// In-VR Help (VRHelpPanel): topics, wording and the text layout maths. Kept
// free of three/React so the layout can be unit tested (tests/vrHelp.test.ts).

export type HelpItem = { k: string; v: string };
export type HelpTopic = { id: string; tab: string; title: string; items: HelpItem[] };

export const HELP_VIDEO = { src: '/help/vr-basics.mp4', poster: '/help/vr-basics.jpg' };

export const HELP_TOPICS: HelpTopic[] = [
  { id: 'controls', tab: 'Getting around', title: 'Getting around with controllers', items: [
    { k: 'Left stick', v: 'Walk.' },
    { k: 'Right stick', v: 'Turn, 30° each push.' },
    { k: 'Trigger', v: 'Press buttons and pick things.' },
    { k: 'Grip', v: 'Grab things you built.' },
    { k: 'A or X', v: 'Open or close your menu.' },
  ] },
  { id: 'hands', tab: 'Hands', title: 'Using your hands', items: [
    { k: 'Press', v: 'Point at a button, pinch thumb and finger.' },
    { k: 'Move', v: 'Point at the floor, pinch and hold, let go to go there. A gold ring on a seat: sit there.' },
    { k: 'Wrist', v: 'Look at the back of your left wrist like a watch: Menu, turn left or right, or Turn around.' },
    { k: 'Gestures', v: 'Wave, clap twice, thumbs up, heart hands or both hands up: your avatar does it too.' },
    { k: 'Menu', v: 'With hands the menu comes closer, so you can press it with your fingers.' },
  ] },
  { id: 'menu', tab: 'Your menu', title: 'Your menu', items: [
    { k: 'Voice', v: 'Mic on or off.' },
    { k: 'Chat, Photo, Selfie', v: 'Room messages and pictures.' },
    { k: 'Sit / Stand up', v: 'Sit on the nearest seat.' },
    { k: 'Height', v: 'Fit your avatar to you. Seated mode raises your view.' },
    { k: 'Emotes', v: 'Wave, dance and lots more.' },
    { k: 'Back to start', v: 'If you get stuck.' },
    { k: 'Home / Leave', v: 'Leave this world.' },
    { k: 'Hide menu', v: 'A, X or your wrist brings it back.' },
  ] },
  { id: 'people', tab: 'People & safety', title: 'People and safety', items: [
    { k: 'People', v: 'The People tab on the side of your menu lists everyone here.' },
    { k: 'Mute', v: 'Stop hearing someone.' },
    { k: 'Block', v: 'Hide someone from you.' },
    { k: 'Report', v: 'Tell us about someone causing trouble.' },
  ] },
  { id: 'comfort', tab: 'Comfort', title: 'Comfort', items: [
    { k: 'Recenter', v: 'View off? Hold the Meta (Oculus) button on the right controller.' },
    { k: 'Breaks', v: 'Take breaks, often.' },
    { k: 'Seated', v: 'Playing sitting down? Turn on Seated mode in your menu.' },
  ] },
];

// troika Text lays out asynchronously, so rows are stacked from an estimate:
// average glyph advance for the default font (Roboto) is ~0.45 em, measured in the emulator. Word wrap is
// approximated greedily (a word never splits across lines).
const EM = .47;
export function estimateLines(text: string, fontSize: number, maxWidth: number) {
  const perLine = Math.max(1, Math.floor(maxWidth / (fontSize * EM)));
  let lines = 1, used = 0;
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const need = used ? used + 1 + word.length : word.length;
    if (need <= perLine) used = need;
    else { lines += 1; used = Math.min(word.length, perLine); }
  }
  return lines;
}

/** Top y of each item (going down from 0) and the total height used. */
export function layoutItems(items: HelpItem[], o: { fontSize: number; keyWidth: number; valueWidth: number; lineHeight: number; gap: number }) {
  const ys: number[] = [];
  let y = 0;
  for (const it of items) {
    ys.push(y);
    const lines = Math.max(estimateLines(it.k, o.fontSize, o.keyWidth), estimateLines(it.v, o.fontSize, o.valueWidth));
    y -= lines * o.fontSize * o.lineHeight + o.gap;
  }
  return { ys, height: -y - (items.length ? o.gap : 0) };
}

export const formatTime = (s: number) => {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60), r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
};
